import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import httpServices from '../../../services/httpServices';
import DashboardLayout from '../../common/DashboardLayout';
import './courseBuilder.css';
import './csvImport.css';


/**
 * Advisory notes on a saved quiz.
 *
 * Everything here is a suggestion. The quiz is already saved by the time this
 * renders, and nothing in it prevents publishing - errors are things that will
 * genuinely mis-grade, while "info" describes the answer key (patterns, option
 * lengths) and is entirely the author's call to act on or ignore.
 */
function ValidationNotes({ validation, onDismiss }) {
  if (!validation || !validation.findings || validation.findings.length === 0) return null;

  const LEVEL = {
    error:   { label: 'Needs fixing', cls: 'vn-error', icon: '⛔',
               blurb: 'These will mis-grade learners or stop the quiz rendering.' },
    warning: { label: 'Worth a look', cls: 'vn-warn', icon: '⚠️',
               blurb: 'Probably a mistake, but you know the material best.' },
    info:    { label: 'For your information', cls: 'vn-info', icon: 'ℹ️',
               blurb: 'Observations about the answer key. Adjust these only if you want to.' }
  };

  const groups = ['error', 'warning', 'info']
    .map((sev) => [sev, validation.findings.filter((f) => f.severity === sev)])
    .filter(([, items]) => items.length > 0);

  return (
    <div className="validation-notes">
      <div className="vn-head">
        <h4>Notes on this quiz</h4>
        <button type="button" className="vn-dismiss" onClick={onDismiss}>Dismiss</button>
      </div>
      <p className="vn-intro">
        Your quiz is saved. None of the following blocks publishing - it is here so you
        can decide what, if anything, to change.
      </p>

      {groups.map(([sev, items]) => (
        <div key={sev} className={`vn-group ${LEVEL[sev].cls}`}>
          <div className="vn-group-head">
            <span className="vn-icon">{LEVEL[sev].icon}</span>
            <strong>{LEVEL[sev].label}</strong>
            <span className="vn-count">{items.length}</span>
          </div>
          <div className="vn-blurb">{LEVEL[sev].blurb}</div>
          <ul>
            {items.map((f, i) => (
              <li key={`${f.code}-${i}`}>
                {f.questionNumber ? <b>Q{f.questionNumber}: </b> : null}
                {f.message}
              </li>
            ))}
          </ul>
        </div>
      ))}
    </div>
  );
}


ValidationNotes.propTypes = {
  validation: PropTypes.shape({
    blocking: PropTypes.bool,
    counts: PropTypes.object,
    findings: PropTypes.arrayOf(PropTypes.shape({
      code: PropTypes.string,
      severity: PropTypes.oneOf(['error', 'warning', 'info']),
      message: PropTypes.string,
      questionNumber: PropTypes.number,
      moduleNumber: PropTypes.number
    }))
  }),
  onDismiss: PropTypes.func.isRequired
};

ValidationNotes.defaultProps = { validation: null };

const CourseBuilder = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEditMode = !!id;

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [validation, setValidation] = useState(null);

  // Course fields
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [expirationMonths, setExpirationMonths] = useState(12);
  const [hours, setHours] = useState('');
  const [isRequired, setIsRequired] = useState(false);
  const [isActive, setIsActive] = useState(false);

  // Audience. `audienceMode` is tracked explicitly rather than inferred from
  // schoolIds.length: unchecking the last school would otherwise silently flip
  // back to "all schools" and publish the course platform-wide on save.
  const [audienceMode, setAudienceMode] = useState('all'); // 'all' | 'selected'
  const [schoolIds, setSchoolIds] = useState([]);
  const [schools, setSchools] = useState([]);
  const [schoolsLoading, setSchoolsLoading] = useState(false);
  const [schoolSearch, setSchoolSearch] = useState('');

  // PDF upload
  const [pdfFile, setPdfFile] = useState(null);
  const [pdfFileName, setPdfFileName] = useState('');
  const [uploadingPdf, setUploadingPdf] = useState(false);

  // Quiz
  const [quizDescription, setQuizDescription] = useState('');
  const [passingScore, setPassingScore] = useState(80);
  const [timeLimit, setTimeLimit] = useState('');
  const [allowRetakes, setAllowRetakes] = useState(true);
  const [questions, setQuestions] = useState([]);

  const [courseId, setCourseId] = useState(null);
  const [quizId, setQuizId] = useState(null);

  useEffect(() => {
    if (isEditMode) {
      fetchCourse();
    }
  }, [id]);

  useEffect(() => {
    fetchSchools();
  }, []);

  const fetchSchools = async () => {
    try {
      setSchoolsLoading(true);
      const response = await httpServices.get('/admin/get-all-schools');
      // The endpoint has returned both a bare array and { schools: [...] } shapes.
      const list = Array.isArray(response)
        ? response
        : response?.schools || response?.data || [];
      setSchools(
        list
          .filter((s) => s && s.id && !s.isDeleted)
          .sort((a, b) => String(a.name || '').localeCompare(String(b.name || '')))
      );
    } catch (err) {
      console.error('Could not load schools for audience picker:', err);
      setSchools([]);
    } finally {
      setSchoolsLoading(false);
    }
  };

  /**
   * Ask the backend how many teachers a required course would reach, then make
   * the admin confirm. Returns false to abort the save.
   */
  const confirmAssignment = async (effectiveSchoolIds) => {
    let teacherCount = null;
    try {
      const qs = effectiveSchoolIds.length
        ? `?schoolIds=${encodeURIComponent(effectiveSchoolIds.join(','))}`
        : '';
      const target = courseId || id || 'new';
      const res = await httpServices.get(
        `/admin/custom-training/courses/${target}/assignment-preview${qs}`
      );
      if (res && res.success) teacherCount = res.teacherCount;
    } catch (err) {
      console.error('Assignment preview failed:', err);
    }

    const scope = effectiveSchoolIds.length
      ? `${effectiveSchoolIds.length} selected school${effectiveSchoolIds.length === 1 ? '' : 's'}`
      : 'EVERY school on the platform';

    const who =
      teacherCount === null
        ? 'all teachers'
        : `${teacherCount} teacher${teacherCount === 1 ? '' : 's'}`;

    return window.confirm(
      `This course is marked Required.\n\n` +
        `It will be added to the dashboard of ${who} across ${scope}.\n\n` +
        `Continue?`
    );
  };

  const toggleSchool = (sid) => {
    setSchoolIds((prev) =>
      prev.includes(sid) ? prev.filter((x) => x !== sid) : [...prev, sid]
    );
  };

  // Auto-resize textareas when questions change
  useEffect(() => {
    const textareas = document.querySelectorAll('textarea.form-control');
    textareas.forEach(textarea => {
      textarea.style.height = 'auto';
      textarea.style.height = textarea.scrollHeight + 'px';
    });
  }, [questions]);

  const fetchCourse = async () => {
    try {
      setLoading(true);
      const response = await httpServices.get(
        `/admin/custom-training/courses/${id}`
      );

      if (response.success) {
        const course = response.course;
        setTitle(course.title);
        setDescription(course.description || '');
        setExpirationMonths(course.expirationMonths);
        setHours(course.hours || '');
        setIsRequired(course.isRequired);
        setIsActive(course.isActive);
        const loadedSchoolIds = Array.isArray(course.schoolIds) ? course.schoolIds : [];
        setSchoolIds(loadedSchoolIds);
        setAudienceMode(loadedSchoolIds.length > 0 ? 'selected' : 'all');
        setPdfFileName(course.pdfFileName || '');
        setCourseId(course.id);

        if (response.quiz) {
          const quiz = response.quiz;
          setQuizId(quiz.id);
          setQuizDescription(quiz.description || '');
          setPassingScore(quiz.passingScore);
          setTimeLimit(quiz.timeLimit || '');
          setAllowRetakes(quiz.allowRetakes);
          setQuestions(quiz.questions || []);
        }
      }
    } catch (error) {
      console.error('Error fetching course:', error);
      setError('Failed to load course');
    } finally {
      setLoading(false);
    }
  };

  const saveCourse = async () => {
    try {
      // Fail closed. Saving "only selected schools" with an empty list would
      // otherwise persist [] and publish the course to every school.
      if (audienceMode === 'selected' && schoolIds.length === 0) {
        setError('Select at least one school, or choose "All schools".');
        return;
      }

      // A required course lands on every audience teacher's dashboard. Show the
      // blast radius first - unscoped + required reaches the whole platform.
      if (isRequired && isActive) {
        const effectiveSchoolIds = audienceMode === 'selected' ? schoolIds : [];
        const ok = await confirmAssignment(effectiveSchoolIds);
        if (!ok) return;
      }

      setSaving(true);
      setError('');
      setSuccess('');

      // Step 1: Upload PDF first if a file is selected
      let uploadedPdfInfo = null;
      if (pdfFile) {
        setUploadingPdf(true);
        const formData = new FormData();
        formData.append('pdf', pdfFile);

        const token = localStorage.getItem('token');
        const apiBaseUrl = httpServices.getApiBaseUrl ? httpServices.getApiBaseUrl() :
          (window.location.hostname.includes('stg.') ? 'https://api.stg.angelwatchedu.org/api' : 'https://api.angelwatchedu.org/api');

        // For edit mode: upload directly to course endpoint
        // For new courses: upload to temp endpoint
        const uploadUrl = isEditMode
          ? `${apiBaseUrl}/admin/custom-training/courses/${id}/upload-pdf`
          : `${apiBaseUrl}/admin/custom-training/upload-temp-pdf`;

        const uploadResponse = await axios.post(
          uploadUrl,
          formData,
          {
            headers: {
              'Content-Type': 'multipart/form-data',
              'Authorization': `Bearer ${token}`
            }
          }
        );

        if (uploadResponse.data.success) {
          uploadedPdfInfo = uploadResponse.data.file;
          setPdfFileName(uploadedPdfInfo.originalName);

          // Previously this redirected straight after the upload, which silently
          // discarded every other pending edit — including the school audience,
          // leaving the course published to all schools while reporting success.
          // Fall through and save the course fields too.
        }
        setUploadingPdf(false);
      }

      // Step 2: Save course with PDF info
      const courseData = {
        title,
        description,
        certificateTitle: title, // Auto-use course name as certificate title
        expirationMonths: parseInt(expirationMonths),
        hours: hours ? parseFloat(hours) : null,
        isRequired,
        isActive,
        schoolIds: audienceMode === 'selected' ? schoolIds : [],
        // Include PDF info if uploaded
        ...(uploadedPdfInfo && {
          pdfFileName: uploadedPdfInfo.originalName,
          pdfUrl: uploadedPdfInfo.url
        })
      };

      let response;
      if (isEditMode) {
        response = await httpServices.put(
          `/admin/custom-training/courses/${id}`,
          courseData
        );
      } else {
        response = await httpServices.post(
          `/admin/custom-training/courses`,
          courseData
        );
      }

      if (response.success) {
        const savedCourseId = response.course.id;
        setCourseId(savedCourseId);
        setPdfFile(null);

        // Auto-save quiz if it exists
        if (questions.length > 0) {
          try {
            const quizData = {
              courseId: savedCourseId,
              title: `${title} Quiz`,
              description: quizDescription,
              passingScore: parseInt(passingScore),
              timeLimit: timeLimit ? parseInt(timeLimit) : null,
              allowRetakes,
              questions
            };

            const quizResponse = quizId
              ? await httpServices.put(`/admin/custom-training/quizzes/${quizId}`, quizData)
              : await httpServices.post(`/admin/custom-training/quizzes`, quizData);

            if (quizResponse.success) {
              setQuizId(quizResponse.quiz.id);
              setValidation(quizResponse.validation || null);

              // Findings are advisory, so the save stands either way. Hold the
              // page when there is something to read rather than navigating
              // away from it after a second.
              const notes = quizResponse.validation?.findings?.length || 0;
              if (notes > 0) {
                setSuccess('Course and quiz saved. Review the notes below.');
                return;
              }
              alert('✅ Course and Quiz saved successfully! Redirecting...');
              setTimeout(() => navigate('/admin/custom-training'), 1000);
              return;
            }
          } catch (quizError) {
            console.error('Quiz save error:', quizError);
            alert('⚠️ Course saved but quiz failed. Please try again.');
          }
        }

        const message = uploadedPdfInfo ?
          '✅ Course and PDF saved successfully!' :
          '✅ Course saved successfully!';
        alert(message);

        if (!isEditMode) {
          navigate(`/admin/custom-training/edit/${savedCourseId}`, { replace: true });
        }
      }
    } catch (error) {
      console.error('Error saving course:', error);
      setError(error.response?.data?.error || 'Failed to save course');
    } finally {
      setSaving(false);
      setUploadingPdf(false);
    }
  };

  const handlePdfUpload = async () => {
    if (!pdfFile || !courseId) return;

    try {
      setUploadingPdf(true);
      setError('');

      const formData = new FormData();
      formData.append('pdf', pdfFile);

      // Use axios directly to avoid httpServices adding extra fields to FormData
      const token = localStorage.getItem('token');
      const apiBaseUrl = httpServices.getApiBaseUrl ? httpServices.getApiBaseUrl() :
        (window.location.hostname.includes('stg.') ? 'https://api.stg.angelwatchedu.org/api' : 'https://api.angelwatchedu.org/api');

      const response = await axios.post(
        `${apiBaseUrl}/admin/custom-training/courses/${courseId}/upload-pdf`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
            'Authorization': `Bearer ${token}`
          }
        }
      );

      if (response.data.success) {
        setPdfFileName(response.data.file.originalName);
        setPdfFile(null);
        alert('✅ PDF uploaded successfully! Redirecting to course list...');
        // Redirect to course list after short delay
        setTimeout(() => {
          navigate('/admin/custom-training');
        }, 1000);
      }
    } catch (error) {
      console.error('Error uploading PDF:', error);
      setError('Failed to upload PDF');
    } finally {
      setUploadingPdf(false);
    }
  };

  const saveQuiz = async () => {
    if (!courseId) {
      setError('Please save the course first');
      return;
    }

    if (questions.length === 0) {
      setError('Please add at least one question');
      return;
    }

    try {
      setSaving(true);
      setError('');

      const quizData = {
        courseId,
        title: `${title} Quiz`, // Auto-generate as "{Course Name} Quiz"
        description: quizDescription,
        passingScore: parseInt(passingScore),
        timeLimit: timeLimit ? parseInt(timeLimit) : null,
        allowRetakes,
        questions
      };

      let response;
      if (quizId) {
        response = await httpServices.put(
          `/admin/custom-training/quizzes/${quizId}`,
          quizData
        );
      } else {
        response = await httpServices.post(
          `/admin/custom-training/quizzes`,
          quizData
        );
      }

      if (response.success) {
        setValidation(response.validation || null);

        const notes = response.validation?.findings?.length || 0;
        if (notes > 0) {
          setSuccess('Quiz saved. Review the notes below.');
          return;
        }

        alert('✅ Quiz saved successfully! Redirecting to course list...');
        // Redirect to course list after short delay
        setTimeout(() => {
          navigate('/admin/custom-training');
        }, 1000);
      }
    } catch (error) {
      console.error('Error saving quiz:', error);
      alert('❌ Failed to save quiz. Please try again.');
      setError('Failed to save quiz');
    } finally {
      setSaving(false);
    }
  };

  const addQuestion = () => {
    setQuestions([
      ...questions,
      {
        id: `q${Date.now()}`,
        question: '',
        type: 'multiple_choice',
        options: ['', '', '', ''],
        correctAnswer: 0,
        explanation: ''
      }
    ]);
  };

  const updateQuestion = (index, field, value) => {
    const updated = [...questions];
    updated[index] = { ...updated[index], [field]: value };
    setQuestions(updated);
  };

  const updateOption = (qIndex, optIndex, value) => {
    const updated = [...questions];
    updated[qIndex].options[optIndex] = value;
    setQuestions(updated);
  };

  const removeQuestion = (index) => {
    setQuestions(questions.filter((_, i) => i !== index));
  };

  const handleCsvImport = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const text = e.target.result;

        // Parse CSV properly - handle newlines inside quotes
        const rows = [];
        let currentRow = [];
        let currentField = '';
        let inQuotes = false;

        for (let i = 0; i < text.length; i++) {
          const char = text[i];
          const nextChar = text[i + 1];

          if (char === '"') {
            if (inQuotes && nextChar === '"') {
              currentField += '"';
              i++;
            } else {
              inQuotes = !inQuotes;
            }
          } else if (char === ',' && !inQuotes) {
            currentRow.push(currentField.trim());
            currentField = '';
          } else if ((char === '\n' || char === '\r') && !inQuotes) {
            if (char === '\r' && nextChar === '\n') i++;
            if (currentField || currentRow.length > 0) {
              currentRow.push(currentField.trim());
              if (currentRow.some(f => f)) rows.push(currentRow);
              currentRow = [];
              currentField = '';
            }
          } else {
            currentField += char;
          }
        }

        if (currentField || currentRow.length > 0) {
          currentRow.push(currentField.trim());
          if (currentRow.some(f => f)) rows.push(currentRow);
        }

        if (rows.length < 2) {
          throw new Error('CSV must have header + at least 1 data row');
        }

        const dataRows = rows.slice(1);
        const importedQuestions = [];

        for (let i = 0; i < dataRows.length; i++) {
          const fields = dataRows[i];

          // Skip number column if present
          let idx = (!isNaN(fields[0]) && fields[0]) ? 1 : 0;

          const question = fields[idx];
          const optionA = fields[idx + 1];
          const optionB = fields[idx + 2];
          const optionC = fields[idx + 3];
          const optionD = fields[idx + 4];
          const correctAnswer = fields[idx + 5];
          const explanation = fields[idx + 6] || '';

          if (!question || !optionA || !optionB || !optionC || !optionD || !correctAnswer) {
            throw new Error(`Row ${i + 2}: Missing fields. Need Question,A,B,C,D,Answer. Found ${fields.length} columns`);
          }

          const ans = correctAnswer.trim().toUpperCase();
          if (!['A', 'B', 'C', 'D'].includes(ans)) {
            throw new Error(`Row ${i + 2}: Answer must be A/B/C/D, got "${correctAnswer}"`);
          }

          importedQuestions.push({
            question: question.trim(),
            type: 'multiple_choice',
            options: [optionA.trim(), optionB.trim(), optionC.trim(), optionD.trim()],
            correctAnswer: ans.charCodeAt(0) - 65,
            explanation: explanation.trim()
          });
        }

        setQuestions(importedQuestions);
        setSuccess(`Imported ${importedQuestions.length} question(s)`);
        event.target.value = '';
      } catch (error) {
        console.error('CSV error:', error);
        setError(`CSV Import Failed: ${error.message}`);
        event.target.value = '';
      }
    };

    reader.onerror = () => {
      setError('Failed to read file');
      event.target.value = '';
    };

    reader.readAsText(file);
  };

  if (loading) {
    return (
      <DashboardLayout role="admin">
        <div className="course-builder">
          <div className="loading-spinner">
            <div className="spinner"></div>
            <p>Loading course...</p>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout role="admin">
      <div className="course-builder">
        <div className="builder-header">
          <div>
            <h1>{isEditMode ? '✏️ Edit Course' : '➕ Create New Course'}</h1>
            <p className="breadcrumb">
              <Link to="/admin/custom-training">Custom Training</Link> / {isEditMode ? 'Edit' : 'Create'}
            </p>
          </div>
          <Link to="/admin/custom-training" className="btn btn-outline">
            ← Back to Courses
          </Link>
        </div>

        {error && <div className="alert alert-danger">{error}</div>}
        {success && <div className="alert alert-success">{success}</div>}
        <ValidationNotes validation={validation} onDismiss={() => setValidation(null)} />

        {/* Unified Course Builder - Single Box with Two Columns */}
        <div className="builder-section">
          <div className="two-column-layout">
            {/* Left Column: Course Information */}
            <div className="left-column">
              <h2>📋 Course Information</h2>

              <div className="form-group">
                <label>Course Title *</label>
                <input
                  type="text"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder="e.g., Child Abuse Awareness Training"
                  className="form-control"
                />
              </div>

              <div className="form-group">
                <label>Description</label>
                <textarea
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="Brief description of the training course"
                  className="form-control"
                  rows="3"
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Training Hours</label>
                  <input
                    type="number"
                    step="0.5"
                    value={hours}
                    onChange={(e) => setHours(e.target.value)}
                    placeholder="e.g., 2.5"
                    className="form-control"
                  />
                </div>
                <div className="form-group">
                  <label>Certificate Expiration (months)</label>
                  <input
                    type="number"
                    value={expirationMonths}
                    onChange={(e) => setExpirationMonths(e.target.value)}
                    className="form-control"
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-check">
                  <input
                    type="checkbox"
                    id="isRequired"
                    checked={isRequired}
                    onChange={(e) => setIsRequired(e.target.checked)}
                  />
                  <label htmlFor="isRequired">Required Training</label>
                </div>

                <div className="form-check">
                  <input
                    type="checkbox"
                    id="isActive"
                    checked={isActive}
                    onChange={(e) => setIsActive(e.target.checked)}
                  />
                  <label htmlFor="isActive">Active (visible to users)</label>
                </div>

                <div className="audience-picker">
                  <label className="audience-label">Who can see this course</label>

                  <div className="form-check">
                    <input
                      type="radio"
                      id="audience-all"
                      name="audience"
                      checked={audienceMode === 'all'}
                      onChange={() => setAudienceMode('all')}
                    />
                    <label htmlFor="audience-all">All schools</label>
                  </div>

                  <div className="form-check">
                    <input
                      type="radio"
                      id="audience-selected"
                      name="audience"
                      checked={audienceMode === 'selected'}
                      onChange={() => setAudienceMode('selected')}
                    />
                    <label htmlFor="audience-selected">Only selected schools</label>
                  </div>

                  {audienceMode === 'selected' && (
                    <div className="audience-list-wrap">
                      <input
                        type="text"
                        className="form-control audience-search"
                        placeholder="Search schools..."
                        value={schoolSearch}
                        onChange={(e) => setSchoolSearch(e.target.value)}
                      />

                      {schoolsLoading && <p className="audience-hint">Loading schools...</p>}

                      <div className="audience-list">
                        {schools
                          .filter((s) =>
                            String(s.name || '')
                              .toLowerCase()
                              .includes(schoolSearch.toLowerCase())
                          )
                          .map((s) => (
                            <div className="form-check" key={s.id}>
                              <input
                                type="checkbox"
                                id={`school-${s.id}`}
                                checked={schoolIds.includes(s.id)}
                                onChange={() => toggleSchool(s.id)}
                              />
                              <label htmlFor={`school-${s.id}`}>{s.name || s.id}</label>
                            </div>
                          ))}
                      </div>

                      {schoolIds.length === 0 ? (
                        <p className="audience-hint audience-hint-warn">
                          Pick at least one school, or switch back to &quot;All schools&quot;.
                        </p>
                      ) : (
                        <p className="audience-hint">
                          {schoolIds.length} school{schoolIds.length === 1 ? '' : 's'} selected.
                          Teachers at every other school will not see this course.
                        </p>
                      )}
                    </div>
                  )}
                </div>
              </div>

            </div>

            {/* Right Column: PDF Upload and Quiz */}
            <div className="right-column">
              {/* PDF Upload Section */}
              <div className="subsection">
                <h3>📄 Training Material (PDF)</h3>

                {pdfFileName && (
                  <div className="current-pdf">
                    <i className="bx bx-file-blank"></i>
                    <span>Current PDF: {pdfFileName}</span>
                  </div>
                )}

                <div className="pdf-upload">
                  <input
                    type="file"
                    accept=".pdf"
                    onChange={(e) => setPdfFile(e.target.files[0])}
                    className="file-input"
                  />
                  {pdfFile && (
                    <div className="file-selected">
                      <span>Selected: {pdfFile.name}</span>
                    </div>
                  )}
                </div>
              </div>

              {/* Quiz Section */}
              <div className="subsection">
                <h3>📝 Quiz</h3>

                <div className="form-row">
                  <div className="form-group">
                    <label>Passing Score (%)</label>
                    <input
                      type="number"
                      min="0"
                      max="100"
                      value={passingScore}
                      onChange={(e) => setPassingScore(e.target.value)}
                      className="form-control"
                    />
                  </div>

                  <div className="form-group">
                    <label>Time Limit (minutes, optional)</label>
                    <input
                      type="number"
                      value={timeLimit}
                      onChange={(e) => setTimeLimit(e.target.value)}
                      placeholder="Leave empty for no limit"
                      className="form-control"
                    />
                  </div>
                </div>

                <div className="form-check">
                  <input
                    type="checkbox"
                    id="allowRetakes"
                    checked={allowRetakes}
                    onChange={(e) => setAllowRetakes(e.target.checked)}
                  />
                  <label htmlFor="allowRetakes">Allow retakes</label>
                </div>

                {/* CSV Import */}
                <div className="csv-import-section">
                  <h4>📥 Import Questions from CSV</h4>
                  <p className="help-text">
                    Format: Question,Option A,Option B,Option C,Option D,Correct Answer,Explanation
                  </p>
                  <input
                    type="file"
                    accept=".csv"
                    onChange={handleCsvImport}
                    id="csv-import"
                    style={{ display: 'none' }}
                  />
                  <label htmlFor="csv-import" className="btn btn-secondary">
                    📄 Choose CSV File
                  </label>
                  {questions.length > 0 && (
                    <span className="question-count"> {questions.length} question(s) loaded</span>
                  )}
                </div>

                <div className="questions-section">
                  <div className="questions-header">
                    <h4>Questions ({questions.length})</h4>
                    <button onClick={addQuestion} className="btn btn-sm btn-primary">
                      + Add Question
                    </button>
                  </div>

                  {questions.map((q, qIndex) => (
                    <div key={q.id} className="question-card">
                      <div className="question-header">
                        <span className="question-number">Question {qIndex + 1}</span>
                        <button onClick={() => removeQuestion(qIndex)} className="btn-icon btn-danger">
                          <i className="bx bx-trash"></i>
                        </button>
                      </div>

                      <div className="form-group">
                        <label>Question Text</label>
                        <textarea
                          value={q.question}
                          onChange={(e) => updateQuestion(qIndex, 'question', e.target.value)}
                          onInput={(e) => {
                            e.target.style.height = 'auto';
                            e.target.style.height = e.target.scrollHeight + 'px';
                          }}
                          placeholder="Enter your question"
                          className="form-control"
                          style={{ minHeight: '60px', overflow: 'hidden' }}
                        />
                      </div>

                      <div className="form-group">
                        <label>Answer Options</label>
                        {q.options.map((opt, optIndex) => (
                          <div key={optIndex} className="option-row">
                            <input
                              type="radio"
                              name={`correct-${qIndex}`}
                              checked={q.correctAnswer === optIndex}
                              onChange={() => updateQuestion(qIndex, 'correctAnswer', optIndex)}
                            />
                            <input
                              type="text"
                              value={opt}
                              onChange={(e) => updateOption(qIndex, optIndex, e.target.value)}
                              placeholder={`Option ${optIndex + 1}`}
                              className="form-control"
                            />
                          </div>
                        ))}
                        <small>Select the radio button for the correct answer</small>
                      </div>

                      <div className="form-group">
                        <label>Explanation (optional)</label>
                        <textarea
                          value={q.explanation}
                          onChange={(e) => updateQuestion(qIndex, 'explanation', e.target.value)}
                          placeholder="Explain why this is the correct answer"
                          className="form-control"
                          rows="2"
                        />
                      </div>
                    </div>
                  ))}
                </div>

                {/* Save Button */}
                <div style={{ marginTop: '30px', textAlign: 'center' }}>
                  <button onClick={saveCourse} disabled={saving || !title} className="btn btn-primary" style={{ minWidth: '200px' }}>
                    {saving ? '⏳ Saving...' : '💾 Save Course'}
                  </button>
                </div>
              </div>
            </div>
          </div>


        </div>
      </div>
    </DashboardLayout>
  );
};

export default CourseBuilder;
